import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { AbstractApiService } from '@app/core/http/abstract-api.service';
import { SanitizerService }   from '@app/core/http/sanitizer.service';
import { TransitionService }  from '@app/core/http/transition.service';
import {
	ITssHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                             from '@app/core/interfaces/api.interface';
import { ModelTypeEnum }      from '@app/core/models/transition.model';
import { FccDatacenter }      from '@app/modules/fcc/fcc-datacenter/models/fcc-datacenter.model';

import { RequestTssModalModeEnum } from '@app/modules/tss/modals/request-tss-modal/request-tss-modal.model';
import {
	Tss,
	TssDeviceTypeEnum,
	TssFccVersions,
	TssFccVersionsCount,
	TssFccVersionsModeCount,
	TssFccVersionsSubtypeModeCount,
	TssModeEnum,
	TssModeSubTypeEnum,
	TssRequest,
	TssStatusEnum
}                                  from '@app/modules/tss/models/tss.model';
import { LoggerService }           from '@app/shared/services/logger.service';
import { MapperService }           from '@http/mapper.service';
import { IHttpContextConfig }      from '@interceptors/interceptors.model';
import { VirtualTssModeEnumDTO }   from '@openApiGenerated/admin-api';
import {
	CreateERSTokenRequestParams,
	CreateTssErsRequestParams,
	CreateTssRequestParams,
	DeleteTssRequestParams,
	ERSTokenDTO,
	FccInstallationTypeEnumDTO,
	GetAllTssRequestParams,
	GetERSTokenRequestParams,
	GetFccPasswordRequestParams,
	GetTssByBusinessPremisesRequestParams,
	GetTssByErsRequestParams,
	GetTssByFccIdRequestParams,
	GetTssRequestParams,
	GetTssVersionCountRequestParams,
	ResetFccPasswordRequestParams,
	SetFinalExportFlagRequestParams,
	TriggerSyncRequestParams,
	TriggerUploadRequestParams,
	TssApiClient,
	TssPasswordDTO,
	UpdateTssRequestParams,
	UpdateTssStatusRequestParams,
	VirtualTssBodyDTO,
	VirtualTssDeviceTypeEnumDTO,
	VirtualTssDTO,
	VirtualTssModeSubtypeEnumDTO,
	VirtualTssRequestDTO,
	VirtualTssStatusDTO,
	VirtualTssVersionsCountResponseDTO
}                                  from '@openApiGenerated/management-api';
import {
	Observable,
	of
}                                  from 'rxjs';
import {
	catchError,
	map
}                                  from 'rxjs/operators';

@Injectable( {
	providedIn: 'root'
} )
export class TssApiService extends AbstractApiService {

	constructor(
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly _tssApiClient: TssApiClient,
		private readonly _mapperService: MapperService,
		private readonly _sanitize: SanitizerService,
		private readonly _transition: TransitionService
	) {
		super();
		this.http = http;
	}

	// SANITIZER ######################################
	private sanitizeApiResponse( entity: Tss, context = this ): void {
		if ( entity instanceof Tss ) {
			entity.activationDate = context._sanitize.date<Date>( entity.activationDate, ModelTypeEnum.TMO, Date );
			entity.createdDate = context._sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.deactivationDate = context._sanitize.date<Date>( entity.deactivationDate, ModelTypeEnum.TMO, Date );

			const deviceType: TssDeviceTypeEnum = !!entity.deviceType
																						? context._mapperService.tss.deviceTypeDtoToTmo( entity.deviceType as unknown as VirtualTssDeviceTypeEnumDTO )
																						: null;

			entity.deviceType = deviceType ?? [ TssModeEnum.TOKEN, TssModeEnum.OFFLINE ].includes( entity.mode as TssModeEnum )
													? TssDeviceTypeEnum.SD
													: entity.mode === TssModeEnum.SWISSBIT_TOKEN
														? deviceType
														: null;

			entity.fccInstallationType = context._mapperService.fcc.installationTypeDtoToTmo( entity.fccInstallationType as unknown as FccInstallationTypeEnumDTO );
			entity.finalUploadReceivedDate = context._sanitize.date<Date>( entity.finalUploadReceivedDate, ModelTypeEnum.TMO, Date );
			entity.lastModifiedDate = context._sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );
			entity.mode = context._mapperService.tss.modeDtoToTmo( entity.mode as unknown as VirtualTssModeEnumDTO );
			entity.modeSubtype = context._mapperService.tss.modeSubtypeDtoToTmo( entity.modeSubtype as unknown as VirtualTssModeSubtypeEnumDTO );

			entity.fccDataCenter = {
				...new FccDatacenter(),
				...entity.fccDataCenter,
				installationTriggerDate: context._sanitize.date<Date>( entity?.fccDataCenter?.installationTriggerDate, ModelTypeEnum.TMO, Date )
			};

			entity.versionUpdateDate = context._sanitize.date<Date>( entity.versionUpdateDate, ModelTypeEnum.TMO, Date );
		}
	}

	private sanitizeTssFccVersionsApiResponse( entity: TssFccVersions, context = this ): void {
		if ( entity instanceof TssFccVersions ) {
			entity.versionModeCounts = entity.versionModeCounts?.map( ( tssFccVersionsCount: TssFccVersionsCount ) => {
				return {
					...tssFccVersionsCount,
					counts: tssFccVersionsCount?.counts.map( ( tssFccVersionsModeCount: TssFccVersionsModeCount ) => {
						return {
							...tssFccVersionsModeCount,
							mode: context._mapperService.tss.modeDtoToTmo( tssFccVersionsModeCount.mode as unknown as VirtualTssModeEnumDTO ),
							subtypes: tssFccVersionsModeCount.subtypes.map( ( tssFccVersionsSubtypeModeCount: TssFccVersionsSubtypeModeCount ) => {
								return {
									...tssFccVersionsSubtypeModeCount,
									modeSubtype: context._mapperService.tss.modeSubtypeDtoToTmo(
										tssFccVersionsSubtypeModeCount.modeSubtype as unknown as VirtualTssModeSubtypeEnumDTO )
								};
							} )
						};
					} )
				};
			} );
		}
	}

	// LIST TYPE REQUESTS #############################
	public list(
		options?: {
			httpContextConfig: IHttpContextConfig,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<Tss>> {
		const {
			httpContextConfig,
			throwError = false
		} = options;

		this.logger.debug( 'pre-list call' );
		const requestParam: GetAllTssRequestParams = {
			sort: 'lastModifiedDate,desc'
		};

		const req$ = this._tssApiClient.getAllTss(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<VirtualTssDTO, Tss>(
			req$,
			Tss,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public search(
		options: {
			httpContextConfig?: IHttpContextConfig,
			params: ITssHttpParams,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<Tss>> {
		const {
			httpContextConfig,
			params,
			throwError = false
		} = options;

		this.logger.debug( 'pre-search call: ', params );
		const requestParam: GetAllTssRequestParams = {
			assignment: this._mapperService.tss.assignmentTypeTmoToDto(
				this.arrayUtils.checkForArrayArgAndReturnAsSingleValue( params?.assignment )
			),
			businessPremisesIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.businessPremisesIds ),
			fccId: this.arrayUtils.checkForArrayArgAndReturnAsSingleValue( params?.fccId ),
			fccInstallation:
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.installationType )
				?.map( this._mapperService.fcc.installationTypeTmoToDto ),
			fccVersion: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.fccVersion ),
			filter: this.arrayUtils.checkForArrayArgAndReturnAsSingleValue( params?.filter ),
			mode:
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.mode )
				?.map( this._mapperService.tss.modeWithSubtypeTmoToDto ),
			organizationIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.organizationIds ),
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.search ),
			sort: params?.sort,
			status: this._mapperService.tss.statusListTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as TssStatusEnum )
			),
			tssIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.tssIds )
		};

		const req$ = this._tssApiClient.getAllTss(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<VirtualTssDTO, Tss>(
			req$,
			Tss,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public listByBPremisesId(
		options: {
			businessPremisesId: string
			httpContextConfig?: IHttpContextConfig,
			params?: ITssHttpParams,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<Tss>> {
		const {
			businessPremisesId,
			httpContextConfig,
			params,
			throwError = false
		} = options;

		this.logger.debug( 'pre-listByBPremisesId call: ', businessPremisesId, params );
		const requestParam: GetTssByBusinessPremisesRequestParams = {
			businessPremisesId,
			assignment: this._mapperService.tss.assignmentTypeTmoToDto(
				this.arrayUtils.checkForArrayArgAndReturnAsSingleValue( params?.assignment )
			),
			fccId: this.arrayUtils.checkForArrayArgAndReturnAsSingleValue( params?.fccId ),
			fccInstallation:
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.installationType )
				?.map( this._mapperService.fcc.installationTypeTmoToDto ),
			fccVersion: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.fccVersion ),
			mode:
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.mode )
				?.map( this._mapperService.tss.modeWithSubtypeTmoToDto ),
			status: this._mapperService.tss.statusListTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as TssStatusEnum )
			),
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			sort: params?.sort
		};

		const req$ = this._tssApiClient.getTssByBusinessPremises(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<VirtualTssDTO, Tss>(
			req$,
			Tss,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	// ENTITY TYPE REQUESTS ###########################
	public listTokens( tss: Tss ): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.debug( 'pre-listTokens call id: ', tss );
		const requestParam: GetERSTokenRequestParams = { tssId: tss.id };

		return this._tssApiClient.getERSToken( requestParam )
		.pipe(
			map( ( res: ERSTokenDTO[] ) => {
				const activationToken = res[ 0 ] ? res[ 0 ].activationToken : null;
				tss = { ...tss, activationToken };
				return this._transition.normalizer.handleEntitySuccessResponse<Tss>( tss, RequestActionEnum.READ );
			} ),
			catchError( error => this._transition.normalizer.handleEntityErrorResponse( error, RequestActionEnum.READ ) )
		);
	}

	public read(
		options: {
			enableRequestPreValidation?: boolean,
			httpContextConfig?: IHttpContextConfig,
			includeAdditionalProperties?: boolean,
			throwError?: boolean,
			tssId: string,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<Tss>> {
		const {
			enableRequestPreValidation,
			includeAdditionalProperties,
			httpContextConfig,
			throwError = false,
			tssId,
			withFullResponse = false
		} = options;

		const requestAction = RequestActionEnum.READ;

		this.logger.debug( 'pre-read call: ', tssId );

		if ( enableRequestPreValidation && !tssId ) {

			const requestState: RequestState = enableRequestPreValidation
																				 ? RequestState.FAILED_REQUEST_PRE_VALIDATION
																				 : null;

			return of( this._transition.getEmptyRequestCompleteEntityState<Tss>( requestAction, requestState ) );
		}

		const requestParam: GetTssRequestParams = {
			tssId,
			includeAdditionalProperties
		};

		const req$ = this._tssApiClient.getTss(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<VirtualTssDTO | HttpResponse<VirtualTssDTO>, Tss>(
			req$,
			requestAction,
			Tss,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public tssByErsId(
		ersId: string,
		throwError = false,
		includeAdditionalProperties: boolean = false
	): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.debug( 'pre-tssByErsId call: ', ersId );
		const requestParam: GetTssByErsRequestParams = {
			ersId,
			includeAdditionalProperties
		};

		const req$ = this._tssApiClient.getTssByErs( requestParam );
		return this._transition.handleEntityRequestResponse<VirtualTssDTO, Tss>(
			req$,
			RequestActionEnum.READ,
			Tss,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public requestTssFor( tssRequest: TssRequest, createTssFor: RequestTssModalModeEnum ): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.debug( 'pre-requestTssFor call: ', createTssFor, tssRequest );

		const virtualTssRequestDTO: VirtualTssRequestDTO = {
			createCspAccount: tssRequest.createCspAccount,
			ersSecret: tssRequest.ersSecret,
			fccInstallationType: this._mapperService.fcc.installationTypeTmoToDto( tssRequest.fccInstallationType ),
			mode: this._mapperService.tss.modeTmoToDto( tssRequest?.mode as TssModeEnum ),
			modeSubtype: this._mapperService.tss.modeSubtypeTmoToDto( tssRequest?.modeSubtype as TssModeSubTypeEnum ) as string,
			name: tssRequest.name,
			triggerFccDataCenterInstallation: tssRequest?.triggerFccDataCenterInstallation
		};

		let req$: Observable<VirtualTssDTO>;
		let requestParam: CreateTssRequestParams | CreateTssErsRequestParams;

		switch ( createTssFor ) {
			case RequestTssModalModeEnum.BUSINESS_PREMISES:
				requestParam = { businessPremisesId: tssRequest.businessPremisesId, virtualTssRequestDTO } as CreateTssRequestParams;
				req$ = this._tssApiClient.createTss( requestParam );
				break;

			case RequestTssModalModeEnum.ERS:
				requestParam = { ersId: tssRequest.ersId, virtualTssRequestDTO } as CreateTssErsRequestParams;
				req$ = this._tssApiClient.createTssErs( requestParam );
				break;
		}

		return this._transition.handleEntityRequestResponse<VirtualTssDTO, Tss>(
			req$,
			RequestActionEnum.CREATE,
			Tss,
			this,
			this.sanitizeApiResponse
		);
	}

	public create(
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			tss: Tss,
			withFullResponse?: boolean
		} ): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.debug( 'pre-create call: ', options?.tss );
		return of( this._transition.getEmptyRequestCompleteEntityState<Tss>( RequestActionEnum.CREATE, RequestState.NOT_SUPPORTED ) );
	}

	public getFccPassword( tss: Tss, throwError = false ): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.log( tss, tss.id );
		const requestParam: GetFccPasswordRequestParams = { tssId: tss.id } as GetFccPasswordRequestParams;
		const req$: Observable<TssPasswordDTO> = this._tssApiClient.getFccPassword( requestParam );


		const preMapper = ( res: TssPasswordDTO ) => {
			const fccSecret = res?.password ? res?.password : null;
			return { ...tss, fccSecret };
		};


		return this._transition.handleEntityRequestResponse<TssPasswordDTO, Tss>(
			req$,
			RequestActionEnum.UPDATE,
			Tss,
			this,
			this.sanitizeApiResponse,
			preMapper,
			null,
			null,
			throwError
		);
	}

	public resetFccPassword( tss: Tss, throwError = false ): Observable<RequestCompleteEntityState<Tss>> {
		const requestParam: ResetFccPasswordRequestParams = { tssId: tss.id } as ResetFccPasswordRequestParams;
		const req$: Observable<TssPasswordDTO> = this._tssApiClient.resetFccPassword( requestParam );

		const preMapper = ( res: TssPasswordDTO ) => {
			const fccSecret = res?.password ? res?.password : null;
			return { ...tss, fccSecret };
		};

		return this._transition.handleEntityRequestResponse<TssPasswordDTO, Tss>(
			req$,
			RequestActionEnum.UPDATE,
			Tss,
			this,
			this.sanitizeApiResponse,
			preMapper,
			null,
			null,
			throwError
		);
	}

	public update(
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			tss: Tss,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<Tss>> {
		const {
			httpContextConfig,
			throwError = false,
			tss,
			withFullResponse = false
		} = options;

		this.logger.debug( 'pre-update call: ', { ...tss } );
		const virtualTssBodyDTO: VirtualTssBodyDTO = {
			ersSecret: tss?.ersSecret,
			fccProperties: tss?.fccProperties,
			name: tss?.name
		};

		const requestParam: UpdateTssRequestParams = {
			tssId: tss.id,
			ifMatch: tss.etag,
			virtualTssBodyDTO
		};

		const req$ = this._tssApiClient.updateTss(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<VirtualTssDTO | HttpResponse<VirtualTssDTO>, Tss>(
			req$,
			RequestActionEnum.UPDATE,
			Tss,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public delete(
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			tssId: string
		}
	): Observable<RequestCompleteEntityState<Tss>> {
		const {
			httpContextConfig,
			throwError = false,
			tssId
		} = options;

		this.logger.debug( 'pre-delete call: ', tssId );
		const requestParam: DeleteTssRequestParams = {
			tssId
		};

		const req$ = this._tssApiClient.deleteTss(
			requestParam,
			'response',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<HttpResponse<any>, Tss>(
			req$,
			RequestActionEnum.DELETE,
			Tss,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public changeStatus(
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			tss: Tss,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<Tss>> {
		const {
			httpContextConfig,
			throwError = false,
			tss,
			withFullResponse = false
		} = options;

		this.logger.debug( 'pre-changeStatus call: ', tss );
		const virtualTssStatusDTO: VirtualTssStatusDTO = {
			status: this._mapperService.tss.statusTmoToDto( tss.status )
		};

		const requestParam: UpdateTssStatusRequestParams = {
			tssId: tss.id,
			ifMatch: tss.etag,
			virtualTssStatusDTO
		};

		const req$ = this._tssApiClient.updateTssStatus(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<VirtualTssDTO | HttpResponse<VirtualTssDTO>, Tss>(
			req$,
			RequestActionEnum.PATCH,
			Tss,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public createTokens( tss: Tss ): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.debug( 'pre-createTokens call: ', `tss: ${ tss }` );
		const requestParam: CreateERSTokenRequestParams = {
			tssId: tss.id
		};

		return this._tssApiClient.createERSToken( requestParam )
		.pipe(
			map( ( res: ERSTokenDTO[] ) => {
				const activationToken = res[ 0 ]?.activationToken;
				tss = { ...tss, activationToken };
				return this._transition.normalizer.handleEntitySuccessResponse<Tss>( tss, RequestActionEnum.CREATE );
			} ),
			catchError( error => this._transition.normalizer.handleEntityErrorResponse( error, RequestActionEnum.CREATE ) )
		);
	}

	public triggerUpload( tss: Tss ): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.debug( 'pre-triggerUpload call: ', tss );
		const requestParam: TriggerUploadRequestParams = {
			tssId: tss.id
		};


		const req$ = this._tssApiClient.triggerUpload( requestParam );
		return this._transition.handleEntityRequestResponse<HttpResponse<any>, Tss>(
			req$,
			RequestActionEnum.UPLOAD,
			Tss,
			this,
			this.sanitizeApiResponse
		);
	}

	public triggerErsSync( tss: Tss ): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.debug( 'pre-triggerSync call: ', tss );
		const requestParam: TriggerSyncRequestParams = {
			tssId: tss.id
		};


		const req$ = this._tssApiClient.triggerSync( requestParam );
		return this._transition.handleEntityRequestResponse<HttpResponse<any>, Tss>(
			req$,
			RequestActionEnum.SYNC,
			Tss,
			this,
			this.sanitizeApiResponse
		);
	}

	public tssByFcc( fccId: string, includeAdditionalProperties: boolean = false ): Observable<RequestCompleteEntityState<Tss>> {
		const requestParam: GetTssByFccIdRequestParams = {
			fccId,
			includeAdditionalProperties
		};

		const req$ = this._tssApiClient.getTssByFccId( requestParam );

		return this._transition.handleEntityRequestResponse<VirtualTssDTO, Tss>(
			req$,
			RequestActionEnum.READ,
			Tss,
			this,
			this.sanitizeApiResponse
		);
	}

	public tssVersions( params: ITssHttpParams ): Observable<RequestCompleteEntityState<TssFccVersions>> {
		const requestParam: GetTssVersionCountRequestParams = {
			fccInstallation:
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.installationType )
				?.map( this._mapperService.fcc.installationTypeTmoToDto ),
			mode:
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.mode )
				?.map( this._mapperService.tss.modeWithSubtypeTmoToDto ),
			status: this._mapperService.tss.statusListTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as TssStatusEnum )
			)
		};

		const req$ = this._tssApiClient.getTssVersionCount( requestParam );
		return this._transition.handleEntityRequestResponse<VirtualTssVersionsCountResponseDTO, TssFccVersions>(
			req$,
			RequestActionEnum.READ,
			TssFccVersions,
			this,
			this.sanitizeTssFccVersionsApiResponse
		);
	}

	public confirmFinalTssLogExport( tss: Tss ): Observable<RequestCompleteEntityState<Tss>> {
		this.logger.debug( 'pre-confirmFinalTssLogExport call: ', tss );
		const requestParam: SetFinalExportFlagRequestParams = {
			tssId: tss.id
		};

		const req$ = this._tssApiClient.setFinalExportFlag( requestParam );
		return this._transition.handleEntityRequestResponse<HttpResponse<any>, Tss>(
			req$,
			RequestActionEnum.CONFIRM,
			Tss,
			this,
			this.sanitizeApiResponse
		);
	}
}
